// Domain types mirrored from backend (src/main/kotlin/.../model/domain/).
// Frontend adds a few runtime-set fields (player.elo, player.filter, player.rank, match.isWin)
// — those are marked optional on the base type.

import type { MatchResult, RoundState, TournamentState } from "../util/Constants";

// ---- ENUMS ----

export type MatchState = MatchResult; // re-export under domain-friendly name
export type TournamentRoundState = RoundState;
export type TournamentTypeValue = "SWISS" | "GROUPS" | "GROUPS_PLAYOFF" | "BLAST";
export type BracketType = "WINNERS" | "LOSERS" | "GRAND_FINAL" | "BRACKET_RESET" | "THIRD_PLACE";

// ---- LADDER ----

export interface Ladder {
  id: number;
  key: string;
  name: string;
  nameEn: string;
  private: boolean;
  createdAt: string;
  updatedAt: string;
  isOfficial?: boolean;
  hidden?: boolean;
  ownerUserId?: number | null;
  calculateElo?: boolean;
  discountCloseLoss?: boolean;
  calculateRating?: boolean;
  awardAchievements?: boolean;
}

export interface UnofficialLadderRow {
  id: number;
  name: string;
  nameEn: string;
  ownerUserId: number | null;
  ownerUsername: string | null;
  tournamentCount: number;
  playerCount: number;
}

export interface LadderOrganizer {
  userId: number;
  username: string;
  playerId: number | null;
  isOwner: boolean;
}

export interface LadderDetail {
  id: number;
  key: string;
  name: string;
  nameEn: string;
  isOfficial: boolean;
  ownerUserId: number | null;
  ownerUsername: string | null;
  calculateElo: boolean;
  discountCloseLoss: boolean;
  calculateRating: boolean;
  awardAchievements: boolean;
  organizers: LadderOrganizer[];
  tournamentCount: number;
  playerCount: number;
}

export interface LadderCreatePayload {
  nameCs: string;
  nameEn: string;
  calculateElo: boolean;
  discountCloseLoss: boolean;
  calculateRating: boolean;
  awardAchievements: boolean;
}

export interface LadderUpdatePayload {
  nameCs?: string;
  nameEn?: string;
  calculateElo?: boolean;
  discountCloseLoss?: boolean;
  calculateRating?: boolean;
  awardAchievements?: boolean;
}

export interface OrganizableLadder {
  id: number;
  name: string;
  nameEn: string;
  isOfficial: boolean;
}

export interface PlayerLadderElo {
  elo: number;
  ladder: Ladder;
}

// ---- PLAYER ----

export interface Player {
  id: number;
  firstname: string;
  lastname: string;
  nickname: string | null;
  ladders: PlayerLadderElo[];
  nationality: string;
  userId: number | null;
  createdAt: string;
  updatedAt: string;

  /** Set by `setPlayerElo()` — ELO for the *current* ladder context. */
  elo?: number;
  /** Set by `playersApi.listByLadder()` — uppercase concat for search matching. */
  filter?: string;
  /** Set by `playersApi.listByLadder()` — 1-based ladder rank. */
  rank?: number;
  /** Set by GroupsOverviewTab — copies PlayerWithSeeding.secondRoundGroup onto score.player for table rendering. */
  secondRoundGroup?: number | null;

  // PlayerEnriched fields — backend includes these on the public listByLadder endpoint.
  firstPlace?: number;
  secondPlace?: number;
  thirdPlace?: number;
  tournamentsPlayed?: number;
}

export interface PlayerWithSeeding {
  seeding: number;
  firstRoundGroup: number | null;
  secondRoundGroup: number | null;
  player: Player;
  onBench: boolean;
}

export interface PlayerScore {
  player: Player;
  score: number;
  matchesWon: number;
  matchesLost: number;
  setsWon: number;
  setsLost: number;
  pointsWon: number;
  pointsLost: number;
  buchholz: number;
  averageRating: number;
}

export interface EloChartPoint {
  date: string;
  opponent: string;
  elo: number;
  win: boolean;
}

export interface PlayerAchievement {
  id: number;
  achievement: string;
  priority: number;
  createdAt: string;
  count?: number; // computed by frontend (compactAchievements)
  dates?: string[]; // computed by frontend
}

export interface PlayerStatistics {
  ladder: Ladder;
  eloChartPoints: EloChartPoint[];
  elo: number;
  achievements: PlayerAchievement[];
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  setsPlayed: number;
  setsWon: number;
  setsLost: number;
  pointsPlayed: number;
  pointsWon: number;
  pointsLost: number;
  tournamentsPlayed: number;
  averagePlacementPercentile: number;
  eloRank: number;
  eloPercentile: number;
  totalPlayers: number;
  averageRating: number;
}

export interface PlayerResult {
  id: number;
  placement: number;
  playerCount: number;
  seeding: number;
  score: number;
  matchesWon: number;
  matchesLost: number;
  setsWon: number;
  setsLost: number;
  pointsWon: number;
  pointsLost: number;
  averageRating: number;
  averageMatchDurationSeconds: number | null;
  tournament: TournamentProjection;
  createdAt: string;
}

/** Response shape of GET /players/:id — superset of Player with stats and history. */
export interface PlayerDetail {
  id: number;
  firstname: string;
  lastname: string;
  nickname: string | null;
  nationality: string;
  userId: number | null;
  username: string | null;
  matches: Match[];
  tournamentResults: PlayerResult[];
  playerStatistics: PlayerStatistics[];
  createdAt: string;
  updatedAt: string;
}

// ---- MATCH ----

/** Per-set score within a match (matches backend `Result` data class). */
export interface SetScore {
  resultOrder: number;
  playerOneScore: number;
  playerTwoScore: number;
}

export interface TournamentProjection {
  id: number;
  name: string;
  ladder: Ladder;
  state: TournamentState;
  type: TournamentTypeValue;
  createdAt: string;
}

export interface Match {
  id: number;
  playerOne: Player;
  playerTwo: Player;
  ladder: Ladder;
  setsNeededToWin: number;
  state: MatchState;
  playerOneEloBeforeMatchFinished: number | null;
  playerTwoEloBeforeMatchFinished: number | null;
  playerOneEloAfterMatchFinished: number | null;
  playerTwoEloAfterMatchFinished: number | null;
  playerOneRating: number | null;
  playerTwoRating: number | null;
  results: SetScore[];
  tournament: TournamentProjection;
  likes: number;
  likedByUser: boolean;
  bracketType: BracketType | null;
  bracketPosition: number | null;
  bracketId: number | null;
  createdAt: string;
  finishedAt: string | null;
  updatedAt: string;
  riskOfDelay: boolean;

  /** Set by `playersApi.getById()` — true when the focused player won. */
  isWin?: boolean;
}

// ---- TOURNAMENT ----

export interface TournamentRound {
  id: number;
  roundOrder: number;
  state: TournamentRoundState;
  matches: Match[];
  scores: PlayerScore[];
  setsNeededToWin: number;
  /** Set when the round transitions to FINISHED; null while it is in progress. */
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TournamentAchievement {
  id: number;
  achievement: string;
  player: Player;
}

export interface Tournament {
  id: number;
  name: string;
  state: TournamentState;
  type: TournamentTypeValue;
  ladder: Ladder;
  organizer: string;
  players: PlayerWithSeeding[];
  setsNeededToWin: number;
  hidden: boolean;
  isDoubleElimination: boolean;
  hasThirdPlaceMatch: boolean;
  hasBracketReset: boolean;
  generationStopped: boolean;
  createdAt: string;
  updatedAt: string;

  // TournamentEnriched fields — present on list/listPaged endpoints, not on detail.
  numberOfPlayers?: number;
  averageElo?: number | null;
  firstPlace?: Player | null;
  secondPlace?: Player | null;
  thirdPlace?: Player | null;
}

export interface TournamentDetail extends Tournament {
  rounds: TournamentRound[];
  finalPlacements: PlayerScore[];
  achievements: TournamentAchievement[];
  topMatches: HallOfFameRecord[];
  blastQueue: Player[];
  /** Whether the current viewer can manage this tournament (admin/creator/organizer). */
  canManage: boolean;
}

export interface PlayerAwardsContext {
  playerId: number;
  priorTournamentsInLadder: number;
  priorTournamentsTotal: number;
  priorBestPlacementInLadder: number | null;
  daysSinceLastInLadder: number | null;
  priorTop3InLadder: number;
  priorWinsInLadder: number;
  isNewEloHighInLadder: boolean;
}

// ---- MATCH DETAIL (for /matches/:id endpoint) ----

export interface ComparisonStats {
  playerOneWinProbability: number;
  playerTwoWinProbability: number;
  playerOneElo: number;
  playerTwoElo: number;
  playerOneForm: string;
  playerTwoForm: string;
  playerOneTotalMatches: number;
  playerTwoTotalMatches: number;
  playerOneAvgRating: number;
  playerTwoAvgRating: number;
  playerOneH2HWins: number;
  playerTwoH2HWins: number;
  playerOneH2HSetsWon: number;
  playerTwoH2HSetsWon: number;
  playerOneH2HPointsWon: number;
  playerTwoH2HPointsWon: number;
  playerOneH2HEloGained: number;
  playerTwoH2HEloGained: number;
  playerOneLastVictoryDate: string | null;
  playerTwoLastVictoryDate: string | null;
}

export interface MatchDetail {
  match: Match;
  comparisonStats: ComparisonStats;
  headToHeadMatches: Match[];
}

// ---- STATS ----

export interface HomepageStats {
  matches: number;
  tournaments: number;
  points: number;
  achievements: number;
  players: number;
}

export interface LadderStatsData {
  totalPlayers: number;
  totalTournaments: number;
  totalMatches: number;
  averageElo: number;
  totalSetsPlayed: number;
  totalPointsPlayed: number;
  highestEloEver: number;
  highestEloPlayer: Player | null;
  mostTournamentsWonPlayer: Player | null;
  mostTournamentsWonCount: number;
  mostTournamentsPlayedPlayer: Player | null;
  mostTournamentsPlayedCount: number;
}

export interface HallOfFameRecord {
  value: string;
  category: string;
  type: "PLAYER" | "MATCH";
  entries: Array<{
    player: Player | null;
    match: Match | null;
  }>;
}

// ---- USERS ----

export interface User {
  id: number;
  username: string;
  role: "USER" | "ADMIN" | "SUPERADMIN";
  playerId: number | null;
}

export interface UnlinkedUser {
  id: number | null; // null for the "no link" sentinel
  username?: string;
}

// ---- CHAT ----

export interface ReactionSummary {
  emoji: string;
  count: number;
  reactedByUser: boolean;
  users: string[];
  userIds: number[];
}

export interface ChatMention {
  playerId: number;
  name: string;
  searchTerms?: string[];
}

export interface ChatReplyPreview {
  id: number;
  authorName: string;
  contentPreview: string;
}

export interface ChatMessage {
  id: number;
  content: string;
  author: Player;
  createdAt: string;
  reactions: ReactionSummary[];
  mentions: ChatMention[];
  pinned?: boolean;
  editedAt?: string | null;
  replyTo?: ChatReplyPreview | null;
  system?: boolean;
  /** Set by `chatApi.getMessages()` — convenience for rendering. */
  authorName?: string;
}

// ---- PAGINATION ----

export interface PagedResponse<T> {
  content: T[];
  totalPages: number;
  number: number;
  totalElements: number;
  size: number;
}

// ---- AUDIT LOG ----

/** Mirrors backend AuditAction enum (model/domain/audit/AuditAction.kt). */
export const AUDIT_ACTIONS = [
  "USER_REGISTERED",
  "USER_PASSWORD_CHANGED",
  "USER_PASSWORD_RESET",
  "USER_ROLE_CHANGED",
  "PLAYER_CREATED",
  "PLAYER_UPDATED",
  "PLAYER_USER_LINK_CHANGED",
  "TOURNAMENT_CREATED",
  "TOURNAMENT_UPDATED",
  "TOURNAMENT_PLAYERS_SET",
  "TOURNAMENT_STARTED",
  "TOURNAMENT_FINISHED",
  "TOURNAMENT_DOUBLE_ELIMINATION_TOGGLED",
  "TOURNAMENT_BLAST_GENERATION_TOGGLED",
  "BLAST_PLAYER_BENCHED",
  "BLAST_PLAYER_RETURNED",
  "ROUND_FINISHED",
  "ROUND_CANCELED",
  "MATCH_RESULT_SET",
  "CHAT_MESSAGE_DELETED",
  "STICKY_NOTE_DELETED",
  "ELO_RECALCULATED",
] as const;

export type AuditAction = typeof AUDIT_ACTIONS[number];

export interface AuditLogEntry {
  id: number;
  createdAt: string;
  userId: number | null;
  username: string | null;
  action: AuditAction;
  entityType: string | null;
  entityId: number | null;
  /** JSON string with action-specific context. May also be null or non-JSON in old rows. */
  details: string | null;
}

export interface StickyNote {
  id: number;
  authorName: string;
  playerId: number | null;
  /** Transparent PNG data URL with the drawn strokes; paper color rendered client-side. */
  imageData: string;
  /** Center position in board units (0..BOARD_WIDTH / 0..BOARD_HEIGHT). */
  x: number;
  y: number;
  rotation: number;
  paperColor: string;
  pinColor: string;
  /** Poll notes render a voting strip below the drawing. */
  poll: boolean;
  votes: StickyNoteVote[];
  createdAt: string;
  /** True when the note belongs to the requesting user. */
  own: boolean;
}

export interface StickyNoteVote {
  /** Voter's user id — dot color derives from it (same palette as chat). */
  userId: number;
  playerId: number | null;
  name: string;
  vote: "YES" | "NO";
  /** True when the vote belongs to the requesting user. */
  own: boolean;
}
